<template>
  <div
    ref="modalEl"
    class="modal fade"
    id="viewBeneficiaryOpeningBalanceModal"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content border-0 shadow-sm overflow-hidden">
        <div class="modal-header bg-white border-bottom">
          <div>
            <h5 class="modal-title mb-0">Beneficiary Opening Balance</h5>
            <div class="text-muted small mt-1">
              View original, adjustments, and effective balance
            </div>
          </div>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
          ></button>
        </div>

        <div class="modal-body bg-light-subtle">
          <div v-if="loading" class="text-center text-muted py-5">
            <i class="bi bi-hourglass-split me-2"></i>Loading…
          </div>

          <div v-else-if="error" class="alert alert-danger py-2 mb-0">
            {{ error }}
          </div>

          <div v-else>
            <!-- Beneficiary Summary -->
            <div class="card border-0 shadow-sm mb-3">
              <div class="card-body">
                <div class="d-flex flex-wrap justify-content-between gap-3">
                  <div>
                    <div class="text-muted small">Beneficiary</div>
                    <div class="fw-semibold fs-5">
                      {{ beneficiary?.name || "—" }}
                    </div>
                    <div class="text-muted small mt-1">
                      Guardian: {{ beneficiary?.guardian_name || "—" }}
                    </div>
                  </div>

                  <div class="text-end">
                    <div class="text-muted small">As of period</div>
                    <div class="fw-semibold">
                      {{ openingBalance?.as_of_period || "—" }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="openingBalance">
              <!-- Financial Summary Cards -->
              <div class="row g-3 mb-3">
                <div class="col-12 col-md-4">
                  <div class="summary-card h-100">
                    <div class="summary-label">Original Balance</div>
                    <div class="summary-value text-dark">
                      {{ formatMoney(openingBalance.original_amount ?? openingBalance.amount) }}
                    </div>
                  </div>
                </div>

                <div class="col-12 col-md-4">
                  <div class="summary-card h-100">
                    <div class="summary-label">Adjustments Total</div>
                    <div
                      class="summary-value"
                      :class="Number(openingBalance.adjustments_total || 0) < 0 ? 'text-danger' : 'text-success'"
                    >
                      {{ formatMoney(openingBalance.adjustments_total ?? 0) }}
                    </div>
                  </div>
                </div>

                <div class="col-12 col-md-4">
                  <div class="summary-card h-100 summary-card-final">
                    <div class="summary-label">Effective Balance</div>
                    <div class="summary-value text-primary">
                      {{ formatMoney(openingBalance.effective_amount ?? openingBalance.amount) }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Details -->
              <div class="card border-0 shadow-sm mb-3">
                <div class="card-body">
                  <div class="row g-3">
                    <div class="col-12 col-md-6">
                      <div class="detail-label">Note</div>
                      <div class="detail-value">
                        {{ openingBalance.note || "—" }}
                      </div>
                    </div>

                    <div class="col-6 col-md-3">
                      <div class="detail-label">Created at</div>
                      <div class="detail-value">
                        {{ formatDateTime(openingBalance.created_at) }}
                      </div>
                    </div>

                    <div class="col-6 col-md-3">
                      <div class="detail-label">Updated at</div>
                      <div class="detail-value">
                        {{ formatDateTime(openingBalance.updated_at) }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Adjustment History -->
              <div class="card border-0 shadow-sm">
                <div class="card-header bg-white border-bottom">
                  <div class="fw-semibold">Adjustment History</div>
                  <div class="text-muted small">
                    All corrections posted against this opening balance
                  </div>
                </div>

                <div class="card-body p-0">
                  <div
                    v-if="openingBalance.adjustments?.length"
                    class="table-responsive"
                  >
                    <table class="table table-sm align-middle mb-0">
                      <thead class="table-light">
                        <tr>
                          <th class="ps-3">Amount</th>
                          <th>Reason</th>
                          <th>Created By</th>
                          <th class="pe-3">Created At</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="a in openingBalance.adjustments"
                          :key="a.id"
                        >
                          <td
                            class="ps-3 fw-semibold"
                            :class="Number(a.amount) < 0 ? 'text-danger' : 'text-success'"
                          >
                            {{ formatMoney(a.amount) }}
                          </td>
                          <td>{{ a.reason || "—" }}</td>
                          <td>{{ a.created_by || "—" }}</td>
                          <td class="pe-3 text-muted">
                            {{ formatDateTime(a.created_at) }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div v-else class="p-4 text-center text-muted">
                    No adjustments recorded.
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="alert alert-warning py-2 mb-0">
              Opening balance not found.
            </div>
          </div>
        </div>

        <div class="modal-footer bg-white border-top">
          <button
            class="btn btn-outline-secondary"
            data-bs-dismiss="modal"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getOpeningBalanceForParticipant } from "@/services/openingBalances";
import { Modal } from "bootstrap";

export default {
  name: "ViewBeneficiaryOpeningBalanceModal",

  data() {
    return {
      modal: null,
      loading: false,
      error: "",
      beneficiary: null,
      openingBalance: null,
    };
  },

  methods: {
    formatMoney(v) {
      const n = Number(v || 0);
      return n.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },

    formatDateTime(v) {
      if (!v) return "—";
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return String(v);
      return d.toLocaleString();
    },

    async open(beneficiary) {
      this.beneficiary = beneficiary || null;
      this.loading = true;
      this.error = "";
      this.openingBalance = null;

      if (!this.modal) {
        this.error = "Modal not initialized.";
        this.loading = false;
        return;
      }

      this.modal.show();

      try {
        if (!this.beneficiary?.id) {
          throw new Error("Beneficiary not provided.");
        }

        const res = await getOpeningBalanceForParticipant({
          ...this.beneficiary,
          participant_type: "beneficiary",
        });

        this.openingBalance = res?.data?.data || null;

        if (!this.openingBalance) {
          this.error = "No opening balance found for this beneficiary.";
        }
      } catch (e) {
        this.error =
          e?.response?.data?.message ||
          e?.message ||
          "Failed to load opening balance.";
      } finally {
        this.loading = false;
      }
    },
  },

  mounted() {
    const el = this.$refs.modalEl;
    if (!el) return;

    this.modal = Modal.getOrCreateInstance(el);

    el.addEventListener("hidden.bs.modal", () => {
      this.loading = false;
      this.error = "";
      this.beneficiary = null;
      this.openingBalance = null;
    });
  },
};
</script>

<style scoped>
.summary-card {
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 14px;
  padding: 1rem;
}

.summary-card-final {
  border-color: #cfe2ff;
  background: #f8fbff;
}

.summary-label {
  font-size: 0.8rem;
  color: #6c757d;
  margin-bottom: 0.35rem;
}

.summary-value {
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.2;
}

.detail-label {
  font-size: 0.8rem;
  color: #6c757d;
  margin-bottom: 0.2rem;
}

.detail-value {
  font-weight: 600;
  color: #212529;
  word-break: break-word;
}

@media (max-width: 576px) {
  :deep(.modal-dialog) {
    margin: 0.75rem;
  }

  .summary-value {
    font-size: 1.15rem;
  }
}
</style>